import * as React from "react";
import theme from "styles/theme";
import List from "@mui/material/List";
import Drawer from "@mui/material/Drawer";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListItemButton from "@mui/material/ListItemButton";
import { useModal } from "hooks";
import { COLORS, ROUTES } from "constant";
import { useLocation, useNavigate } from "react-router";
import {
  Avatar,
  Box,
  CardMedia,
  CircularProgress,
  ListItemIcon,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "context/authContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export const Sidebar = (props: any) => {
  const { currentUser, setCurrentUser, isLoggedIn, isPending, loading } =
    useAuth();

  const isSmUp = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [activePath, setActivePath] = React.useState<string>(pathname);
  const [isAdminOpen, setIsAdminOpen] = React.useState<boolean>(
    pathname.startsWith(ROUTES.DISCOVER) ||
    pathname.startsWith(ROUTES.USERS) ||
    pathname.startsWith(ROUTES.CONTENTS) ||
    pathname.startsWith(ROUTES.PENDING_REFUND) ||
    pathname.startsWith(ROUTES.PENDING_VERIFICATION)
  );

  const handleBack = () => {
    props.onClose();
  };

  const handleLogout = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleContentCreator = () => {
    setActivePath(ROUTES.BECOME_CONTENT_CREATOR);
    navigate(ROUTES.BECOME_CONTENT_CREATOR);
  };

  const allStepsCompleted = () => {
    const userInfo = currentUser?.userInfo || {};
    return (
      userInfo.biography &&
      userInfo.degrees &&
      userInfo.certifications &&
      userInfo.licenseNumber &&
      userInfo.insuranceNumber &&
      userInfo.twitter
    );
  };

  type ListItemProps = {
    text?: string;
    path: string;
    active?: string[];
    icon?: string;
    style?: React.CSSProperties;
    isSubItem?: boolean;
  };

  const handleNavigation = (path: string) => {
    if (path === "") {
      // Toggling the Admin dropdown
      setIsAdminOpen(!isAdminOpen);
    } else {
      // Only update active path and navigate for actual routes
      setActivePath(path);
      navigate(path);

      // Do not close the dropdown if navigating to an Admin subitem
      // if (![ROUTES.USERS, ROUTES.CONTENTS, ROUTES.PENDING_REFUND, ROUTES.PENDING_VERIFICATION].includes(path)) {
      //   setIsAdminOpen(false);
      // }
    }
  };

  const generateListItem = ({
    text,
    path,
    active,
    icon,
    style,
    isSubItem,
  }: ListItemProps) => {
    return (
      <ListItem key={path}>
        <ListItemButton
          onClick={() => handleNavigation(path)}
          selected={active?.includes(pathname)}
          sx={{
            pl: isSubItem ? 3 : 2,
          }}
        >
          {/* Conditionally render the icon only if it exists */}
          {icon && (
            <CardMedia
              sx={{
                width: { xs: "13.87px", md: "20px" },
                height: { xs: "13.87px", md: "20px" },
                objectFit: "cover",
              }}
              image={icon}
            />
          )}
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              fontWeight: 700,
              fontSize: { xs: "14px", md: "18px" },
              color: COLORS.dark.light,
            }}
            sx={{
              ml: icon ? 0 : "-16px", // Move text left if no icon is present
            }}
          />
          {path === "" && (
            <>
              {isAdminOpen ? (
                <KeyboardArrowUpIcon
                  sx={{
                    marginLeft: "auto",
                    color: "#93989A",
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    marginLeft: "auto",
                    color: "#93989A",
                  }}
                />
              )}
            </>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      {/* Show 'Discover' and 'Login/Register' only if user is not logged in */}
      {!isLoggedIn ? (
        <></>
      ) : (
        <>
          <Drawer
            variant={isSmUp ? "permanent" : "temporary"}
            {...props}
            sx={{
              "& .MuiDrawer-paper": {
                // height: "100vh",
                backgroundColor: "#F8F8F8",

                position: "relative !important",
                borderTopLeftRadius: {
                  xs: "30px",
                  md: 0,
                },
                marginTop: {
                  md: "-10px",
                  xs: 0,
                },
              },
            }}
          >
            <List
              className="MuiList-sideBar-menu"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ListItem
                sx={{
                  my: 4,
                  display: "flex",
                  alignItems: { xs: "center", md: "center" },
                  justifyContent: { xs: "space-between", md: "center" },
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/mobSidebarLogo.svg"
                  alt="logo"
                  sx={{
                    width: { xs: "70px", md: "112px" },
                    height: { xs: "34.17px", md: "35.78px" },
                    objectFit: "contain",
                    pl: { xs: "20px", md: "0px" },
                    cursor: "pointer",
                  }}
                ></Box>
                <Box
                  onClick={handleBack}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    justifyContent: "flex-end",
                    cursor: "pointer",
                    mr: { xs: "15px", md: "0px" },
                  }}
                >
                  <CloseIcon
                    sx={{
                      width: { xs: "16.59px", md: "0px" },
                      height: { xs: "16.59px", md: "0px" },
                      color: "#A2A2A2",
                    }}
                  />
                </Box>
              </ListItem>
              {generateListItem({
                text: "Discover",
                path: ROUTES.DISCOVER,
                active: [ROUTES.DISCOVER],
                icon: "/assets/icons/affhome.png",
              })}
              {generateListItem({
                text: "My Learning",
                path: ROUTES.MY_LEARNING,
                active: [ROUTES.MY_LEARNING],
                icon: "/assets/icons/afflearning.png",
              })}
              {generateListItem({
                text: "Payments",
                path: ROUTES.PAYMENT_MANAGEMENT,
                active: [ROUTES.PAYMENT_MANAGEMENT],
                icon: "/assets/icons/affpayments.png",
              })}
              {generateListItem({
                text: "Settings",
                path: ROUTES.SETTINGS,
                active: [ROUTES.SETTINGS],
                icon: "/assets/icons/affsettings.png",
              })}

              <Box
                border={"2px solid #00A79D"}
                borderRadius={"10px"}
                m={4}
              ></Box>

              {currentUser?.userInfo?.type === "content-creator" &&
                currentUser?.userInfo?.accountStatus === "approve" ? (
                <>
                  {generateListItem({
                    text: "My Content",
                    path: ROUTES.MY_CONTENT,
                    active: [ROUTES.MY_CONTENT],
                    icon: "/assets/icons/affmycontent.png",
                  })}
                  {generateListItem({
                    text: "My Earnings",
                    path: ROUTES.MY_EARNINGS,
                    active: [ROUTES.MY_EARNINGS],
                    icon: "/assets/icons/affmyearning.png",
                  })}
                </>
              ) : (
                <Box>
                  {!currentUser?.userInfo?.isAdmin && (
                    <>
                      {isPending ? (
                        <Typography
                          sx={{
                            color: "#fff",
                            fontSize: "18px",
                            fontWeight: 700,
                            ml: 2,
                            cursor: "default",
                            backgroundColor: "#F79E1B",
                            borderRadius: "10px",
                            padding: "10px 10px",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Content Creator Account Pending
                        </Typography>
                      ) : (
                        <Box onClick={handleContentCreator}>
                          <ListItemButton
                            onClick={() => {
                              setActivePath(ROUTES.BECOME_CONTENT_CREATOR);
                              navigate(ROUTES.BECOME_CONTENT_CREATOR);
                            }}
                            selected={
                              activePath === ROUTES.BECOME_CONTENT_CREATOR
                            }
                            sx={{
                              pl: 2,
                              width: "100%",
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word", // Ensure long words break properly
                              background:
                                activePath === ROUTES.BECOME_CONTENT_CREATOR
                                  ? COLORS.gradient.main
                                  : "transparent",
                              borderLeft:
                                activePath === ROUTES.BECOME_CONTENT_CREATOR
                                  ? `7.76px solid ${COLORS.primary.hard}`
                                  : "none",
                              "&.Mui-selected, &.Mui-selected:hover": {
                                background: COLORS.gradient.main,
                                borderLeft: `7.76px solid ${COLORS.primary.hard}`,
                              },
                              "&:hover": {
                                boxShadow: "none",
                                // background: COLORS.success.light,
                                // borderLeft: `7.76px solid ${COLORS.primary.hard}`,
                              },
                            }}
                          >
                            <ListItemText
                              primary="Become a Content Creator"
                              primaryTypographyProps={{
                                fontWeight: 700,
                                fontSize: { xs: "14px", md: "18px" },
                                color: COLORS.dark.light,
                                whiteSpace: "normal",
                                // wordBreak: "break-word",
                                textAlign: "left",
                              }}
                              sx={{
                                ml: 2,
                                maxWidth: "160px", // Adjust width to ensure text wraps to two lines
                              }}
                            />
                          </ListItemButton>

                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {currentUser?.userInfo?.isAdmin ? (
                <>
                  {generateListItem({
                    text: "Admin",
                    path: "",
                    icon: "/assets/icons/affadmin.png",
                  })}
                  {isAdminOpen && (
                    <>
                      {generateListItem({
                        text: "Users",
                        path: ROUTES.USERS,
                        active: [ROUTES.USERS],
                        icon: "/assets/icons/affuser.png",
                        isSubItem: true,
                      })}
                      {generateListItem({
                        text: "Contents",
                        path: ROUTES.CONTENTS,
                        active: [ROUTES.CONTENTS],
                        icon: "/assets/icons/affcontent.png",
                        isSubItem: true,
                      })}

                      {generateListItem({
                        text: "Pending Verifications",
                        path: ROUTES.PENDING_VERIFICATION,
                        active: [ROUTES.PENDING_VERIFICATION],
                        icon: "/assets/icons/affpendingverification.png",
                        isSubItem: true,
                      })}
                    </>
                  )}
                </>
              ) : (
                ""
              )}


              <Box
                onClick={handleLogout}
                display={{ xs: "flex", md: "none" }}
                color={"#233F91"}
                fontSize={"14px"}
                fontWeight={700}
                gap={1}
                alignContent={"center"}
                justifyContent={"center"}
                pt={6}
              >
                <img src="/assets/icons/sidebarLogout.svg" alt="logout" />
                LogOut
              </Box>
            </List>
          </Drawer>
        </>
      )}
    </>
  );
};

export default Sidebar;
